// Seekbar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { color } from '../style/variables.js';
import { hover } from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

export class Seekbar extends React.PureComponent {
  state = {
    progressBarRect: null,
    isSeeking: false,
    startClientX: 0,
    endClientX: 0,
    scaleX: 0,
  };
  resizeTimeout = null;
  scaleXInterval = null;

  bufferedScale = 0;
  hasTransition = null;
  hasMax = true;

  changeProgressBar = ({ clientX }) => {
    const { onChange } = this.props;
    const { progressBarRect } = this.state;
    if (progressBarRect) {
      const { left, width } = progressBarRect;
      const result = ((clientX - left) / width).toFixed(9);
      if (result <= 0) {
        onChange({ positionPercentage: 0 });
      } else if (result >= 1) {
        onChange({ positionPercentage: 1 });
      } else {
        if (this.hasMax) {
          onChange({ positionPercentage: result });
        } else {
          onChange({
            positionPercentage: result,
            sliderPosition: (clientX - left) * 10,
          });
          this.setState({
            scaleX: clientX - left,
          });
        }
      }
    }
  };
  handleTouchStart = event => {
    if (event.touches[0]) {
      const { clientX } = event.touches[0];
      const { onSeekStart } = this.props;
      this.setState({
        isSeeking: true,
        startClientX: clientX,
        endClientX: clientX,
        progressBarRect: this.progressBar.getBoundingClientRect(),
      });
      onSeekStart(event);
    }
  };
  handleTouchMove = event => {
    if (event.touches[0]) {
      const { clientX } = event.touches[0];
      const { onSeeking } = this.props;
      const { isSeeking } = this.state;
      if (isSeeking) {
        this.setState({
          endClientX: clientX,
        });
        this.changeProgressBar({ clientX });
        onSeeking(event);
      }
    }
  };
  handleTouchEnd = event => {
    const { onSeekEnd } = this.props;
    const { endClientX } = this.state;
    this.setState({
      isSeeking: false,
    });
    this.changeProgressBar({ clientX: endClientX });
    onSeekEnd(event);
  };
  handleMouseDown = event => {
    const { clientX } = event;
    const { onSeekStart } = this.props;
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    this.setState({
      isSeeking: true,
      startClientX: clientX,
      endClientX: clientX,
      progressBarRect: this.progressBar.getBoundingClientRect(),
    });
    onSeekStart(event);
  };
  handleMouseMove = event => {
    const { clientX } = event;
    const { onSeeking } = this.props;
    const { isSeeking } = this.state;
    if (isSeeking) {
      this.setState({
        endClientX: clientX,
      });
      this.changeProgressBar({ clientX });
      onSeeking(event);
    }
  };
  handleMouseUp = event => {
    const { onSeekEnd } = this.props;
    const { endClientX } = this.state;
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    this.setState({
      isSeeking: false,
    });
    this.changeProgressBar({ clientX: endClientX });
    onSeekEnd(event);
  };
  onResize = () => {
    this.resizeTimeout = setTimeout(() => {
      if (this.progressBar) {
        this.setState({
          progressBarRect: this.progressBar.getBoundingClientRect(),
        });
      }
    }, 1000); // TODO: remote config
  };

  updateScaleXInterval() {
    this.scaleXInterval = setInterval(() => {
      this.setState(({ scaleX }) => ({
        scaleX: scaleX + 0.1,
      }));
    }, 1000);
  }

  handleSlider() {
    const { isSeeking, scaleX } = this.state;
    const { min, max, value, bufferedTime } = this.props;
    if (Number.isFinite(max)) {
      this.setState({
        scaleX: ((value - min) / (max - min)).toFixed(9) * 100,
      });
      this.bufferedScale =
        ((bufferedTime - min) / (max - min)).toFixed(9) * 100;
      this.hasTransition = scaleX < 99.999 && scaleX > 0.05 && !isSeeking;
      this.hasMax = true;
    } else {
      this.bufferedScale = bufferedTime * 0.1;
      this.hasTransition = scaleX >= 0.05 && !isSeeking;
      this.hasMax = false;
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);

    this.handleSlider();
    if (!this.hasMax) this.updateScaleXInterval();
  }

  componentDidUpdate() {
    this.handleSlider();

    const { progressBarRect, scaleX } = this.state;
    if (this.scaleXInterval && progressBarRect) {
      const { width } = progressBarRect;
      if (scaleX >= width) clearInterval(this.scaleXInterval);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
    clearInterval(this.scaleXInterval);
  }

  render() {
    const {
      thumbIconSource,
      thumbSize,
      seekbarHeight,
      currentValueColor,
      isShowHoverFeedback,
    } = this.props;
    const { progressBarRect, scaleX } = this.state;
    const seekbarMaxWidth = progressBarRect?.width || 10000;

    return (
      <StyledSeekbar>
        <MaxValue
          seekbarHeight={seekbarHeight}
          ref={element => {
            this.progressBar = element;
          }}
          onTouchStart={this.handleTouchStart}
          onTouchMove={this.handleTouchMove}
          onTouchEnd={this.handleTouchEnd}
          onMouseDown={this.handleMouseDown}
        >
          {!!this.bufferedScale && (
            <BufferedBar
              scaleX={this.bufferedScale}
              maxWidth={seekbarMaxWidth}
              hasMax={this.hasMax}
              hasTransition
            />
          )}
          <CurrentValue
            seekbarHeight={seekbarHeight}
            maxWidth={seekbarMaxWidth}
            scaleX={scaleX}
            hasTransition={this.hasTransition}
            hasMax={this.hasMax}
            currentValueColor={currentValueColor}
          >
            <Thumb
              size={thumbSize}
              iconSource={thumbIconSource}
              isShowHoverFeedback={isShowHoverFeedback}
            />
          </CurrentValue>
        </MaxValue>
      </StyledSeekbar>
    );
  }
}

Seekbar.propTypes = {
  thumbIconSource: PropTypes.string,
  thumbSize: PropTypes.number,
  seekbarHeight: PropTypes.number,
  bufferedTime: PropTypes.number,
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  currentValueColor: PropTypes.string,
  isShowHoverFeedback: PropTypes.bool,
  /**
   * value percentage change
   *
   * @param {object} payload - payload
   * @param {float} payload.positionPercentage - percentage at new position
   */
  onChange: PropTypes.func,
  /**
   * on seek start event
   *
   * @param {object} event - event object
   */
  onSeekStart: PropTypes.func,
  /**
   * on seeking event.
   * this event will trigger onChange event also.
   *
   * @param {object} event - event object
   */
  onSeeking: PropTypes.func,
  /**
   * on seek end event
   * this event will trigger onChange event also.
   *
   * @param {object} event - event object
   */
  onSeekEnd: PropTypes.func,
};

Seekbar.defaultProps = {
  thumbIconSource: '',
  thumbSize: 16,
  seekbarHeight: 4,
  bufferedTime: 0,
  value: 0,
  min: 0,
  max: 0,
  currentValueColor: '',
  isShowHoverFeedback: false,
  onChange: () => null,
  onSeekStart: () => null,
  onSeeking: () => null,
  onSeekEnd: () => null,
};

const StyledSeekbar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const MaxValue = styled.div.attrs(({ seekbarHeight }) => ({
  style: {
    height: `${seekbarHeight}px`,
  },
}))`
  position: relative;
  border-radius: 2px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.3);

  ::after {
    position: absolute;
    top: -8px;
    bottom: -8px;
    left: 0;
    right: 0;
    display: block;
    content: '';
  }
`;

const barStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 2px;
  height: ${({ seekbarHeight }) => seekbarHeight}px;
  will-change: width;

  ${({ hasTransition }) =>
    hasTransition ? 'transition: width 0.1s linear;' : null};
`;

const BufferedBar = styled.div.attrs(props => ({
  style: {
    width: props.hasMax ? `${props.scaleX}%` : `${props.scaleX}px`,
    maxWidth: `${props.maxWidth}px`,
  },
}))`
  ${barStyle};
  background-color: ${color.white};
  opacity: 0.5;
`;

const CurrentValue = styled.div.attrs(props => ({
  style: {
    width: props.hasMax ? `${props.scaleX}%` : `${props.scaleX}px`,
    maxWidth: `${props.maxWidth}px`,
    backgroundColor: props.currentValueColor
      ? props.currentValueColor
      : color.tealBlue,
  },
}))`
  ${barStyle};
`;

const Thumb = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 0px;
  border-radius: 50%;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background-color: ${color.white};
  transform: translate(50%, -50%);
  ${({ iconSource }) =>
    iconSource
      ? css`
          border-radius: unset;
          background-color: unset;
          background-image: url(${iconSource});
          background-position: center;
          background-size: contain;
          background-repeat: no-repeat;
        `
      : css`
          box-shadow: 0px 0px 4px 0px ${color.neutral['0_alpha_20']};
        `};
  ${({ isShowHoverFeedback }) =>
    isShowHoverFeedback &&
    css`
      transition: box-shadow 0.1s ease-in-out;
      ${hover`
        box-shadow: 0 0 0 6px ${color.neutral['100_alpha_20']};
      `}
    `};
  ::after {
    position: absolute;
    top: -4px;
    bottom: -4px;
    left: -4px;
    right: -4px;
    display: block;
    content: '';
  }
`;

export default Seekbar;
